import { useMemo, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import { cn } from '@/lib/cn';
import { LoadingState, EmptyState, ErrorState } from './States';

/**
 * The DataTable primitive from the UI modernization assessment (backlog #12)
 * — "nine raw tables; no sticky head, sort, density or keyboard rows". Built
 * on top of Table.tsx's plain shell rather than replacing it: a screen that
 * just needs th/td chrome keeps using Table/TableRow/TableCell directly (see
 * that file's own header comment), this is for a screen that additionally
 * wants sort, density, sticky head and keyboard-navigable rows without
 * hand-rolling all four again per screen.
 *
 * Deliberately client-side sort only (no server-side sort param, no
 * pagination) — every current call site loads its full list already; adding
 * either would be scope creep against what the raw tables it replaces
 * actually did. Revisit only if a screen's row count genuinely needs it (see
 * the assessment's own "revisit TanStack Table if DataTable needs
 * virtualization" note).
 *
 * No `style` prop anywhere in here, including for column width or a
 * scrollable max-height — `eslint.config.mjs`'s backlog-16 gate bans new
 * inline styles in `components/ui/**`. A column takes `widthClassName`
 * (a literal Tailwind utility written in the caller's own source, e.g.
 * `"w-24"` or `"w-[90px]"`, so Tailwind's JIT scanner can see it) instead of
 * a raw CSS width; a scrollable sticky-header table takes a `max-h-*`
 * utility through the ordinary `className` prop on the outer wrapper.
 *
 * `rowClassName` exists for a per-row severity signal (QueueView.tsx's
 * priority stripe, assessment backlog #18) that has to live on the `<tr>`
 * itself, not a cell — a coloured left border reads across the whole row at
 * a glance, which is the point of a stripe over a same-weight badge column.
 */

export interface DataTableColumn<T> {
  key: string;
  header: ReactNode;
  /** Renders a cell. Defaults to a no-op empty cell if omitted (header-only spacer column). */
  render?: (row: T) => ReactNode;
  align?: 'left' | 'right' | 'center';
  /** Enables the click-to-sort header. Requires `sortValue`. */
  sortable?: boolean;
  sortValue?: (row: T) => string | number | null | undefined;
  /** A Tailwind width utility (e.g. `"w-24"`), not a raw CSS value — see the header's own note on why this can't be a `style` prop. */
  widthClassName?: string;
  className?: string;
  headerClassName?: string;
}

export interface DataTableProps<T> {
  columns: DataTableColumn<T>[];
  rows: T[];
  rowKey: (row: T) => string | number;
  onRowClick?: (row: T) => void;
  /** Per-row classes (e.g. a left severity stripe via `border-l-4 border-l-danger`) merged onto the `<tr>`. */
  rowClassName?: (row: T) => string | undefined;
  density?: 'comfortable' | 'compact';
  /** `default` — uppercase tracked headers, card frame. `sheet` — the claims
   * wireframes' `table.t`: title-case 11px bold headers on a soft band,
   * tight 5–6px cells and row rules (Claims_Desk_Screens_Wireframe.html). */
  appearance?: 'default' | 'sheet';
  isLoading?: boolean;
  isError?: boolean;
  errorMessage?: string;
  onRetry?: () => void;
  emptyLabel?: string;
  emptyHint?: string;
  initialSort?: { key: string; direction: 'asc' | 'desc' };
  className?: string;
}

const ALIGN_CLASS: Record<NonNullable<DataTableColumn<unknown>['align']>, string> = {
  left: 'text-left',
  right: 'text-right tabular-nums',
  center: 'text-center',
};

export function DataTable<T>({
  columns,
  rows,
  rowKey,
  onRowClick,
  rowClassName,
  density = 'comfortable',
  appearance = 'default',
  isLoading,
  isError,
  errorMessage,
  onRetry,
  emptyLabel = 'Nothing to show',
  emptyHint,
  initialSort,
  className,
}: DataTableProps<T>) {
  const [sort, setSort] = useState<{ key: string; direction: 'asc' | 'desc' } | null>(initialSort ?? null);
  const rowRefs = useRef<Array<HTMLTableRowElement | null>>([]);

  const sortedRows = useMemo(() => {
    if (!sort) return rows;
    const col = columns.find((c) => c.key === sort.key);
    if (!col?.sortValue) return rows;
    const dir = sort.direction === 'asc' ? 1 : -1;
    return [...rows].sort((a, b) => {
      const av = col.sortValue!(a);
      const bv = col.sortValue!(b);
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      if (av < bv) return -1 * dir;
      if (av > bv) return 1 * dir;
      return 0;
    });
  }, [rows, sort, columns]);

  const toggleSort = (col: DataTableColumn<T>) => {
    if (!col.sortable) return;
    setSort((prev) => {
      if (prev?.key !== col.key) return { key: col.key, direction: 'asc' };
      if (prev.direction === 'asc') return { key: col.key, direction: 'desc' };
      return null;
    });
  };

  const focusRow = (index: number) => {
    const clamped = Math.max(0, Math.min(sortedRows.length - 1, index));
    rowRefs.current[clamped]?.focus();
  };

  const handleRowKeyDown = (event: KeyboardEvent<HTMLTableRowElement>, index: number, row: T) => {
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        focusRow(index + 1);
        break;
      case 'ArrowUp':
        event.preventDefault();
        focusRow(index - 1);
        break;
      case 'Home':
        event.preventDefault();
        focusRow(0);
        break;
      case 'End':
        event.preventDefault();
        focusRow(sortedRows.length - 1);
        break;
      case 'Enter':
      case ' ':
        if (onRowClick) {
          event.preventDefault();
          onRowClick(row);
        }
        break;
    }
  };

  if (isLoading) return <LoadingState label="Loading…" />;
  if (isError) return <ErrorState message={errorMessage ?? 'Failed to load'} onRetry={onRetry} />;
  if (!rows.length) return <EmptyState label={emptyLabel} hint={emptyHint} />;

  const sheet = appearance === 'sheet';
  const cellPad = sheet ? 'px-1.5 py-[5px]' : density === 'compact' ? 'px-3 py-1.5' : 'px-3 py-2.5';

  return (
    <div className={cn('relative w-full overflow-auto rounded-xl border border-border', className)}>
      <table className="w-full caption-bottom text-sm">
        <thead className={cn('sticky top-0 z-10', sheet ? 'bg-muted' : 'bg-card')}>
          <tr className="border-b border-border">
            {columns.map((col) => {
              const active = sort?.key === col.key;
              return (
                <th
                  key={col.key}
                  scope="col"
                  className={cn(
                    sheet
                      // Explicit case/tracking/background: index.css's legacy
                      // bare `th` rule uppercases and colours headers itself.
                      ? 'h-auto bg-muted align-top text-[11px] font-bold normal-case tracking-normal text-ink-muted'
                      : 'h-9 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground',
                    cellPad,
                    ALIGN_CLASS[col.align ?? 'left'],
                    col.widthClassName,
                    col.headerClassName
                  )}
                >
                  {col.sortable ? (
                    <button
                      type="button"
                      onClick={() => toggleSort(col)}
                      className={cn(
                        'inline-flex items-center gap-1 hover:text-ink',
                        sheet && '[font:inherit] normal-case tracking-normal',
                        !sheet && 'uppercase tracking-wide',
                        active && 'text-ink'
                      )}
                      aria-sort={active ? (sort!.direction === 'asc' ? 'ascending' : 'descending') : 'none'}
                    >
                      {col.header}
                      {(active || !sheet) && (
                        <span className="text-[9px] leading-none opacity-70">
                          {active ? (sort!.direction === 'asc' ? '▲' : '▼') : '⇅'}
                        </span>
                      )}
                    </button>
                  ) : (
                    col.header
                  )}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {sortedRows.map((row, index) => {
            const clickable = !!onRowClick;
            return (
              <tr
                key={rowKey(row)}
                ref={(el) => { rowRefs.current[index] = el; }}
                tabIndex={clickable ? 0 : undefined}
                role={clickable ? 'button' : undefined}
                onClick={clickable ? () => onRowClick(row) : undefined}
                onKeyDown={clickable ? (e) => handleRowKeyDown(e, index, row) : undefined}
                className={cn(
                  'border-b border-border transition-colors last:border-0 hover:bg-muted/50',
                  clickable && 'cursor-pointer focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-teal/25 focus-visible:relative focus-visible:z-10',
                  rowClassName?.(row)
                )}
              >
                {columns.map((col, colIndex) => (
                  <td
                    key={col.key}
                    className={cn(
                      sheet ? 'align-top text-[12.5px]' : 'align-middle',
                      cellPad,
                      ALIGN_CLASS[col.align ?? 'left'],
                      col.className,
                      // A border-left set on the <tr> itself doesn't render
                      // under `table { border-collapse: collapse }` (a legacy
                      // index.css rule this table inherits, since preflight
                      // isn't loaded — see theme.css's own comment) in most
                      // browsers, even though a border-bottom on the same
                      // <tr> renders fine. Re-applying rowClassName onto the
                      // first cell is what actually makes a left stripe (e.g.
                      // QueueView's priority stripe) visible; harmless for a
                      // rowClassName that's just a background tint, which
                      // renders correctly either way.
                      colIndex === 0 && rowClassName?.(row)
                    )}
                  >
                    {col.render ? col.render(row) : null}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
